/**
 * Inline `{ et, en }` i18n - the component is self-contained (it does not depend on
 * either app's I18nProvider), like the widgets. [[i18n-no-hardcoded-languages]]
 */
// Extension-ful specifier on purpose: apps/api typechecks this file under NodeNext
// resolution (it imports the labels in `entitlements.labels.test.ts`), and NodeNext
// rejects an extensionless relative import. Vite resolves "./types.js" to types.ts.
import { UNLIMITED, type EntitlementValue, type Locale, type LocaleMap } from "./types.js";

type Dict = Record<string, { et: string; en: string }>;

const STRINGS: Dict = {
  // ── Common ─────────────────────────────────────────────────────────────────
  loading: { et: "Laen…", en: "Loading…" },
  save: { et: "Salvesta", en: "Save" },
  cancel: { et: "Loobu", en: "Cancel" },
  delete: { et: "Kustuta", en: "Delete" },
  edit: { et: "Muuda", en: "Edit" },
  close: { et: "Sulge", en: "Close" },
  none: { et: "-", en: "-" },
  unlimited: { et: "∞", en: "∞" },
  search: { et: "Otsi…", en: "Search…" },
  all: { et: "Kõik", en: "All" },

  // ── Packages ────────────────────────────────────────────────────────────────
  "packages.title": { et: "Paketid", en: "Packages" },
  "packages.subtitle": { et: "Loo ja hinnasta pakette ning halda funktsioone.", en: "Create, price and configure packages." },
  "packages.new": { et: "Uus pakett", en: "New package" },
  "packages.empty": { et: "Ühtegi paketti pole veel.", en: "No packages yet." },
  "packages.public": { et: "Avalik", en: "Public" },
  "packages.hidden": { et: "Peidetud", en: "Hidden" },
  "packages.subscribers": { et: "tellijat", en: "subscribers" },
  "packages.sites": { et: "saiti seires", en: "watched sites" },
  "packages.perMonth": { et: "/kuus", en: "/mo" },
  "packages.perYear": { et: "/aastas", en: "/yr" },
  "packages.features": { et: "funktsiooni", en: "features" },
  "packages.free": { et: "Tasuta", en: "Free" },

  // ── Products/services ─────────────────────────────────────────────────────────
  "products.all": { et: "Kõik teenused", en: "All services" },
  "products.platform": { et: "Veebihaldus", en: "Web management" },
  "products.manage": { et: "Halda teenuseid", en: "Manage services" },
  "products.title": { et: "Teenused", en: "Services" },
  "products.subtitle": { et: "Grupeeri pakette teenuse järgi (nt veebihaldus, veebimajutus).", en: "Group packages by service (e.g. web management, hosting)." },
  "products.new": { et: "Uus teenus", en: "New service" },
  "products.empty": { et: "Ühtegi teenust pole veel.", en: "No services yet." },
  "products.platformBadge": { et: "Platvorm", en: "Platform" },
  "products.displayBadge": { et: "Kuva-ainult", en: "Display-only" },
  "products.monitoringBadge": { et: "Saidiseire", en: "Site monitoring" },
  "products.plans": { et: "paketti", en: "packages" },
  "products.field.product": { et: "Teenus", en: "Service" },
  "products.field.icon": { et: "Ikoon", en: "Icon" },
  "products.iconHint": { et: "lucide ikooni-nimi, nt server, globe", en: "lucide icon name, e.g. server, globe" },
  "products.displayHint": { et: "Kuva-ainult teenus - paketid kuvatakse hinnana saidil/pakkumistel, aga ei lukusta platvormi funktsioone.", en: "Display-only service - packages show as pricing on the site/quotes but don't gate platform features." },
  "products.monitoringHint": { et: "Selle teenuse paketid juhivad saidiseiret: seiratava saidi kontrolli sagedus ja jälgimised tulevad tema paketist. Avalik hinnaleht näitab ainult punktiloendit.", en: "This service's packages drive site monitoring: a watched site's check interval and looks come from its package. The public price page shows only the bullet list." },

  // ── Icon picker (lucide popover) ──────────────────────────────────────────────
  "icon.pick": { et: "Vali ikoon", en: "Pick icon" },
  "icon.clear": { et: "Eemalda", en: "Clear" },
  "icon.search": { et: "Otsi ikooni…", en: "Search icons…" },
  "icon.popular": { et: "Populaarsed", en: "Popular" },
  "icon.results": { et: "tulemust", en: "results" },
  "icon.total": { et: "ikooni", en: "icons" },
  "icon.noResults": { et: "Ühtegi ikooni ei leitud", en: "No icons found" },
  "icon.loadMore": { et: "Lae rohkem", en: "Load more" },

  // ── Services master-detail page ───────────────────────────────────────────────
  "services.title": { et: "Teenused", en: "Services" },
  "services.subtitle": { et: "Halda teenuseid ja nende pakette. Üks teenus on platvorm, Veebihalduse paketid juhivad saidiseiret ja ülejäänud on kuva-ainult hinnakataloogid.", en: "Manage services and their packages. One service is the platform, the Web management packages drive site monitoring and the rest are display-only pricing catalogs." },
  "services.new": { et: "Uus teenus", en: "New service" },
  "services.empty": { et: "Ühtegi teenust pole veel - loo esimene.", en: "No services yet - create the first one." },
  "services.back": { et: "Tagasi teenuste juurde", en: "Back to services" },
  "services.packages": { et: "Selle teenuse paketid", en: "Packages in this service" },
  "services.settings": { et: "Teenuse seaded", en: "Service settings" },
  "services.editService": { et: "Muuda teenust", en: "Edit service" },
  "services.deleteService": { et: "Kustuta teenus", en: "Delete service" },
  "services.openService": { et: "Ava", en: "Open" },
  "products.editTitle": { et: "Muuda teenust", en: "Edit service" },
  "products.newTitle": { et: "Uus teenus", en: "New service" },
  "products.err.codeTaken": { et: "See kood on juba kasutusel.", en: "That code is already taken." },
  "products.err.inUse": { et: "Teenusel on pakette - kustuta või liiguta need esmalt.", en: "Service has packages - delete or move them first." },
  "products.err.platformProtected": { et: "Platvormi-teenust ei saa kustutada.", en: "The platform service can't be deleted." },
  "products.err.generic": { et: "Salvestamine ebaõnnestus.", en: "Save failed." },
  "products.deleteConfirm": { et: "Kustuta see teenus?", en: "Delete this service?" },

  // ── Package editor ───────────────────────────────────────────────────────────
  "editor.newTitle": { et: "Uus pakett", en: "New package" },
  "editor.editTitle": { et: "Muuda paketti", en: "Edit package" },
  "editor.code": { et: "Kood (muutumatu)", en: "Code (immutable)" },
  "editor.codeHint": { et: "nt premium, business - a-z, 0-9, -, _", en: "e.g. premium, business - a-z, 0-9, -, _" },
  "editor.name": { et: "Nimi", en: "Name" },
  "editor.desc": { et: "Kirjeldus", en: "Description" },
  "ml.linked": { et: "Lukus - sisestus mõjutab kõiki keeli korraga. Klõpsa avamiseks.", en: "Linked - input affects all languages at once. Click to unlink." },
  "ml.unlinked": { et: "Lahti - iga keel eraldi. Klõpsa lukustamiseks (sünkroonib praegusest keelest).", en: "Unlinked - each language separate. Click to link (syncs from current language)." },
  "ml.added": { et: "Sisu lisatud", en: "Content added" },
  "ml.missing": { et: "Sisu puudub", en: "Content missing" },
  "editor.priceMonthly": { et: "Kuuhind (€)", en: "Monthly price (€)" },
  "editor.priceYearly": { et: "Aastahind (€, kokku)", en: "Yearly price (€, total)" },
  "editor.currency": { et: "Valuuta", en: "Currency" },
  "editor.isPublic": { et: "Avalik (kuvatakse plaanivalikus)", en: "Public (shown in plan picker)" },
  "editor.sortOrder": { et: "Järjekord", en: "Sort order" },
  "editor.trialDays": { et: "Prooviperiood (päeva)", en: "Trial period (days)" },
  "editor.trialDaysHint": { et: "0 = väljas. Rakendub uue tellimuse checkout'is (kaart nõutav).", en: "0 = off. Applies at new-subscription checkout (card required)." },
  "editor.featuresTitle": { et: "Funktsioonid (entitlement'id)", en: "Features (entitlements)" },
  "editor.featuresHint": { et: "Lülitid mõjutavad kohe paketi väravaid.", en: "Toggles affect plan gating immediately." },
  "editor.monitorTitle": { et: "Saidiseire", en: "Site monitoring" },
  "editor.monitorHint": { et: "Mida saidiseire selle paketi saitidele teeb. Muudatus kehtib järgmisest kontrollist.", en: "What site monitoring does for the sites on this package. A change applies from the next check." },
  "editor.planned": { et: "tulekul", en: "planned" },
  // ── Display/marketing (display-only packages) ─────────────────────────────────
  "editor.highlightsTitle": { et: "Mida pakett sisaldab", en: "What's included" },
  "editor.highlightsHint": { et: "Punktiloend kuvatakse paketi-kaardil ja avalikul hinnalehel.", en: "Bullet list shown on the package card and the public pricing page." },
  "editor.highlightAdd": { et: "Lisa rida", en: "Add item" },
  "editor.highlightPlaceholder": { et: "nt Igapäevased varundused", en: "e.g. Daily backups" },
  "editor.highlightRemove": { et: "Eemalda rida", en: "Remove" },
  "editor.featured": { et: "Esiletõstetud kaart", en: "Featured card" },
  "editor.badge": { et: "Silt (valikuline)", en: "Badge (optional)" },
  "editor.badgePlaceholder": { et: "nt Populaarseim", en: "e.g. Most popular" },
  "packages.highlights": { et: "punkti", en: "items" },
  "editor.deleteConfirm": { et: "Kustuta see pakett?", en: "Delete this package?" },
  "editor.err.codeTaken": { et: "See kood on juba kasutusel.", en: "That code is already taken." },
  "editor.err.inUse": { et: "Paketti kasutavad tellimused - määra need esmalt teisele paketile.", en: "Subscriptions use this package - reassign them first." },
  "editor.err.inUseSites": { et: "Saidiseire kasutab seda paketti - vaheta seiratavate saitide pakett esmalt ära.", en: "Site monitoring uses this package - move the watched sites to another package first." },
  "editor.err.defaultProtected": { et: "Vaikeplaani ei saa kustutada.", en: "The default plan can't be deleted." },
  "editor.err.generic": { et: "Salvestamine ebaõnnestus.", en: "Save failed." },

  // ── Subscriptions ────────────────────────────────────────────────────────────
  "subs.title": { et: "Tellimused", en: "Subscriptions" },
  "subs.subtitle": { et: "Kellel mis pakett on - käsitsi määramine kuni Stripe valmib.", en: "Who has which package - manual assignment until Stripe." },
  "subs.totalSites": { et: "Kliendi-saite", en: "Client sites" },
  "subs.mrr": { et: "Kuukäive (MRR)", en: "Monthly revenue (MRR)" },
  "subs.listPrice": { et: "Nimekirjahind", en: "List price" },
  "subs.sitesPerPlan": { et: "Saite paketi kohta", en: "Sites per package" },
  "subs.empty": { et: "Tellimusi ei leitud.", en: "No subscriptions found." },
  "subs.col.site": { et: "Sait", en: "Site" },
  "subs.col.owner": { et: "Omanik", en: "Owner" },
  "subs.col.plan": { et: "Pakett", en: "Package" },
  "subs.col.status": { et: "Staatus", en: "Status" },
  "subs.col.cycle": { et: "Tsükkel", en: "Cycle" },
  "subs.col.periodEnd": { et: "Periood lõpeb", en: "Period ends" },
  "subs.col.addons": { et: "Lisad", en: "Add-ons" },
  "subs.col.created": { et: "Loodud", en: "Created" },
  "subs.noOwner": { et: "Omanik puudub", en: "No owner" },
  "subs.implicitFree": { et: "vaikimisi", en: "default" },
  "subs.assign": { et: "Määra pakett", en: "Assign package" },
  "subs.refund": { et: "Maksa tagasi", en: "Refund" },
  "subs.refundConfirmTitle": { et: "Maksa raha tagasi?", en: "Issue a refund?" },
  "subs.refundConfirmBody": { et: "See maksab „{site}” konto viimase arve täies ulatuses tagasi ja katkestab tellimuse kohe. Seda saab teha üks kord.", en: "This refunds the last invoice of “{site}” in full and cancels the subscription immediately. This can be done once." },
  "subs.refundDone": { et: "Tagasimakse tehtud.", en: "Refund issued." },
  "subs.refundFailed": { et: "Tagasimakse ebaõnnestus.", en: "Refund failed." },
  "subs.assignFor": { et: "Paketi määramine", en: "Assign package" },
  "subs.resolvedFeatures": { et: "Kehtivad funktsioonid", en: "Effective features" },
  "subs.members": { et: "Liikmed", en: "Members" },
  "subs.field.plan": { et: "Pakett", en: "Package" },
  "subs.field.status": { et: "Staatus", en: "Status" },
  "subs.field.cycle": { et: "Arveldustsükkel", en: "Billing cycle" },
  "subs.field.periodEnd": { et: "Perioodi lõpp", en: "Period end" },
  "subs.field.trialEnd": { et: "Prooviperioodi lõpp", en: "Trial end" },
  "subs.field.cancelAtEnd": { et: "Tühista perioodi lõpus", en: "Cancel at period end" },

  // ── Stripe payment gateway (F2) ──────────────────────────────────────────────
  "subs.stripe.title": { et: "Makselüüs (Stripe)", en: "Billing (Stripe)" },
  "subs.stripe.configured": { et: "Stripe seadistatud", en: "Stripe configured" },
  "subs.stripe.notConfigured": { et: "Stripe pole seadistatud - lisa võtmed .env.local-i", en: "Stripe not configured - add keys to .env.local" },
  "subs.stripe.syncedSuffix": { et: "paketti sünkitud", en: "plans synced" },
  "subs.stripe.sync": { et: "Sünk Stripe'i", en: "Sync to Stripe" },
  "subs.stripe.syncing": { et: "Sünkroonin…", en: "Syncing…" },
  "subs.stripe.syncDone": { et: "Paketid sünkroonitud Stripe'i", en: "Plans synced to Stripe" },
  "subs.stripe.syncFailed": { et: "Sünk ebaõnnestus", en: "Sync failed" },
  "subs.stripe.badge": { et: "Stripe", en: "Stripe" },
  "subs.stripe.manual": { et: "Käsitsi", en: "Manual" },
  "subs.stripe.openCustomer": { et: "Ava Stripe'is", en: "Open in Stripe" },

  // ── Statuses ─────────────────────────────────────────────────────────────────
  "status.trialing": { et: "Prooviperiood", en: "Trialing" },
  "status.active": { et: "Aktiivne", en: "Active" },
  "status.past_due": { et: "Maksevõlg", en: "Past due" },
  "status.canceled": { et: "Tühistatud", en: "Canceled" },
  "status.paused": { et: "Peatatud", en: "Paused" },

  // ── Cycles ───────────────────────────────────────────────────────────────────
  "cycle.monthly": { et: "Kuine", en: "Monthly" },
  "cycle.yearly": { et: "Aastane", en: "Yearly" },

  // ── Categories ───────────────────────────────────────────────────────────────
  "cat.publishing": { et: "Avaldamine", en: "Publishing" },
  "cat.content": { et: "Sisu", en: "Content" },
  "cat.modules": { et: "Moodulid", en: "Modules" },
  "cat.ai": { et: "Husly AI", en: "Husly AI" },
  "cat.builder": { et: "Veebiehitaja", en: "Builder" },
  "cat.support": { et: "Tugi", en: "Support" },
  "cat.monitoring": { et: "Seire", en: "Monitoring" },
  "cat.wordpress": { et: "WordPress", en: "WordPress" },
  "cat.client": { et: "Kliendile", en: "For the client" },

  // ── Plan comparison (PlanComparison cards + table) ───────────────────────────
  "compare.everythingPre": { et: "Kõik, mis ", en: "Everything in " },
  "compare.everythingPost": { et: " paketis, pluss:", en: ", plus:" },
  "compare.includes": { et: "Sisaldab:", en: "Includes:" },
  "compare.showTable": { et: "Näita täielikku võrdlust", en: "Show full comparison" },
  "compare.hideTable": { et: "Peida võrdlus", en: "Hide comparison" },
  "compare.featureCol": { et: "Funktsioon", en: "Feature" },
  // Exclusive-widget tiers - the plan card shows one line per tier instead of listing
  // fourteen widget names (see `widgetTierLabel`).
  "compare.widgetTier.1": { et: "Efekt-widgetid", en: "Effect widgets" },
  "compare.widgetTier.2": { et: "Signatuur-widgetid", en: "Signature widgets" },
  "compare.widgetTier.3": { et: "Lipulaev-widgetid", en: "Flagship widgets" },
  "card.currentBadge": { et: "Praegune", en: "Current" },
  "card.currentBtn": { et: "Praegune pakett", en: "Current plan" },
  "card.upgradeBtn": { et: "Uuenda", en: "Upgrade" },
  "card.downgradeBtn": { et: "Vali madalam", en: "Downgrade" },
  "card.switchToYearly": { et: "Mine aastaarveldusele", en: "Switch to yearly" },
  "card.switchToMonthly": { et: "Mine kuuarveldusele", en: "Switch to monthly" },
  "card.includedFree": { et: "Tasuta kaasas", en: "Included free" },
  "card.popular": { et: "Populaarseim", en: "Most popular" },
  "card.perMonth": { et: "kuus", en: "mo" },
  "card.billedYearly": { et: "Arveldatakse aastas", en: "Billed yearly" },
  "card.free": { et: "Tasuta", en: "Free" },
  // Trial CTA (shown on a paid plan when it has trialDays and the trial is unused).
  "card.trialCta": { et: "Proovi tasuta", en: "Try for free" },
  "card.trialHint": { et: "päeva tasuta · kaart nõutav", en: "days free · card required" },
  "value.unlimited": { et: "Piiramatu", en: "Unlimited" },
  "value.yes": { et: "Jah", en: "Yes" },

  // ── Plan-change confirm dialog (proration before billing) ────────────────────
  "confirm.title.upgrade": { et: "Kinnita uuendus", en: "Confirm upgrade" },
  "confirm.title.downgrade": { et: "Kinnita muudatus", en: "Confirm change" },
  "confirm.loading": { et: "Arvutan proportsiooni…", en: "Calculating proration…" },
  "confirm.dueToday": { et: "Tasud kohe", en: "Due today" },
  "confirm.then": { et: "Seejärel", en: "Then" },
  "confirm.perMonthSuffix": { et: "/kuus", en: "/month" },
  "confirm.perYearSuffix": { et: "/aastas", en: "/year" },
  "confirm.from": { et: "alates", en: "from" },
  "confirm.breakdown": { et: "Jaotus", en: "Breakdown" },
  // Localized labels for proration invoice lines (Stripe only provides them in
  // English) - assembled in `planChangeLineLabel`: "<time> <on> <plan> <after> <date>".
  "confirm.lineUnused": { et: "Kasutamata aeg", en: "Unused time" },
  "confirm.lineRemaining": { et: "Järelejäänud aeg", en: "Remaining time" },
  "confirm.lineOn": { et: "paketil", en: "on" },
  "confirm.lineAfter": { et: "pärast", en: "after" },
  "confirm.downgradeCredit": { et: "Kasutamata krediit rakendub järgmisele arvele.", en: "Unused credit will be applied to your next invoice." },
  "confirm.quotaWarningPre": { et: "Sul on ", en: "You have " },
  "confirm.quotaWarningMid": { et: " saiti, aga uus pakett lubab ", en: " sites, but the new plan allows " },
  "confirm.quotaWarningPost": { et: ". Olemasolevad saidid jäävad tööle, kuid uusi ei saa juurde luua enne, kui oled kvoodi piiresse jõudnud.", en: ". Existing sites keep working, but you can't add new ones until you're back within the quota." },
  "confirm.noChange": { et: "See pakett on sama mis praegune - midagi ei muutu.", en: "This plan matches your current one - nothing to change." },
  "confirm.error": { et: "Proportsiooni ei õnnestunud arvutada. Saad siiski jätkata.", en: "Couldn't calculate the proration. You can still continue." },
  // Config error (e.g. the cycle's price is not synced to the payment gateway) - "Continue
  // anyway" would not help, because the real change would fail the same way. Hence a
  // separate message and only "Close".
  "confirm.errorConfig": { et: "Selle arveldustsükli hind pole makselüüsis veel seadistatud, seega vahetust ei saa praegu teha. Võta ühendust toega.", en: "This billing cycle isn't set up in the payment provider yet, so the change can't be made right now. Please contact support." },
  "confirm.confirm": { et: "Kinnita", en: "Confirm" },
  "confirm.continue": { et: "Jätka ikkagi", en: "Continue anyway" },
  "confirm.retry": { et: "Proovi uuesti", en: "Retry" },
  "confirm.cancel": { et: "Loobu", en: "Cancel" },
  "confirm.close": { et: "Sulge", en: "Close" },
  // On a yearly cycle the unused remainder is large and is NOT a payout - say so, so nobody
  // expects money back on their card.
  "confirm.downgradeCreditYearly": {
    et: "Aastasel tsüklil võib krediit olla suur. Seda saab kasutada ainult tulevastel Husly arvetel - tellimuse lõpetamisel seda välja ei maksta.",
    en: "On a yearly cycle the credit can be large. It can only be spent on future Husly invoices - it is not paid out if you cancel.",
  },
  "confirm.trialNoCharge": {
    et: "Prooviperioodi ajal tasu ei võeta - uus hind hakkab kehtima proovi lõppedes.",
    en: "Nothing is charged during your trial - the new price starts when the trial ends.",
  },

  // -- Quota resolution in the plan-change dialog ------------------------------
  "quota.title": { et: "Saidikvoot", en: "Site quota" },
  "quota.headline": {
    et: "Sul on {active} aktiivset saiti, aga see pakett katab {allowed}.",
    en: "You have {active} active sites, but this plan covers {allowed}.",
  },
  "quota.optionAddOn": { et: "Hoia kõik saidid alles", en: "Keep all your sites" },
  "quota.optionAddOnHint": { et: "Lisa {n} × „+1 sait\" - {price}", en: "Add {n} × \"+1 site\" - {price}" },
  "quota.optionKeep": { et: "Loobu osadest saitidest", en: "Give up some sites" },
  "quota.optionKeepHint": {
    et: "Vali {n}, mis jäävad tööle. Ülejäänud peatatakse.",
    en: "Choose {n} to stay live. The rest are suspended.",
  },
  "quota.pickTitle": { et: "Vali, millised saidid jäävad tööle", en: "Choose which sites stay live" },
  "quota.pickCounter": { et: "Valitud {n}/{max}", en: "Selected {n}/{max}" },
  "quota.suspendNote": {
    et: "Peatatud sait ei ole külastajale nähtav, kuid kogu sisu säilib ja sait taastub kohe, kui kvoot uuesti kasvab.",
    en: "A suspended site is not visible to visitors, but all its content is kept and it comes back as soon as the quota grows again.",
  },
  "quota.noAddOn": {
    et: "Selle paketiga ei saa lisasaiti juurde osta, seega tuleb valida, millised saidid jäävad tööle.",
    en: "This plan can't take extra sites, so you need to choose which ones stay live.",
  },
  "quota.pickRequired": { et: "Vali saidid, mis jäävad tööle.", en: "Choose the sites that stay live." },

  // -- "What do I lose?" (limits below usage + features switching off) ---------
  "impact.title": { et: "Mida see muudab", en: "What this changes" },
  "impact.line": { et: "{feature}: kasutuses {used}, uus lagi {limit}", en: "{feature}: {used} in use, new cap {limit}" },
  "impact.onSite": { et: "saidil {site}", en: "on {site}" },
  "impact.keepsWorking": {
    et: "Olemasolev sisu jääb alles - piiratakse ainult uut juurdetulekut.",
    en: "Existing content stays - only new usage is limited.",
  },
  "impact.lost": { et: "Kaob: {features}", en: "You lose: {features}" },

  // -- Add-on change dialog (quantity up/down/removed) -------------------------
  "addOn.confirmTitleAdd": { et: "Kinnita lisa", en: "Confirm add-on" },
  "addOn.confirmTitleRemove": { et: "Eemalda lisa", en: "Remove add-on" },
  "addOn.newPrice": { et: "Uus hind", en: "New price" },
  "addOn.removeCredit": {
    et: "Kasutamata aeg läheb krediidina järgmisele arvele - kaardile raha tagasi ei kanta.",
    en: "Unused time becomes credit on your next invoice - nothing is refunded to your card.",
  },
  "addOn.addCharge": {
    et: "Jooksva perioodi osa arveldatakse kohe.",
    en: "The remainder of the current period is billed now.",
  },
  "addOn.keepRequired": {
    et: "See eemaldus jätab ühe või mitu saiti kvoodist välja. Vali, millised jäävad tööle.",
    en: "This removal leaves one or more sites outside the quota. Choose which ones stay live.",
  },
};

/**
 * Fill `{name}` placeholders in a translation. `t` takes only a key (like the rest of the
 * plans i18n), so parameterized sentences go through this - it avoids splitting a sentence
 * into Pre/Mid/Post keys, which breaks in translation.
 */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key]) : match));
}

/** Short noun labels for card rows (without the "(max)" suffix). */
const FEATURE_SHORT_LABELS: Dict = {
  "sites.max": { et: "Saidid", en: "Sites" },
  "pages.max": { et: "Lehed", en: "Pages" },
  "storage.mb": { et: "Salvestusruum", en: "Storage" },
  "locales.max": { et: "Keeled", en: "Languages" },
  "ai.credits.monthly": { et: "AI-krediiti kuus", en: "AI credits/mo" },
  "ai.images.monthly": { et: "AI-pilte kuus", en: "AI images/mo" },
  "ai.videos.monthly": { et: "AI-videoid kuus", en: "AI videos/mo" },
  "ai.voice.charsMonthly": { et: "Häältähemärke kuus", en: "Voice characters/mo" },
  "automation.recipes.max": { et: "Automaatikaid", en: "Automations" },
  "automation.credits.monthly": { et: "Autopiloodi krediiti kuus", en: "Autopilot credits/mo" },
};

/** Human-readable labels for feature keys (the catalog is stable, in code). */
const FEATURE_LABELS: Dict = {
  "publish.customDomain": { et: "Oma domeen", en: "Custom domain" },
  "branding.remove": { et: "Eemalda Husly-bränding", en: "Remove Husly branding" },
  "sites.max": { et: "Saite (max)", en: "Sites (max)" },
  "pages.max": { et: "Lehti (max)", en: "Pages (max)" },
  "storage.mb": { et: "Maht (MB)", en: "Storage (MB)" },
  "locales.max": { et: "Keeli (max)", en: "Languages (max)" },
  "templates.premium": { et: "Premium-mallid", en: "Premium templates" },
  "analytics.basic": { et: "Põhianalüütika", en: "Basic analytics" },
  "module.blog": { et: "Blogi-moodul", en: "Blog module" },
  "module.forms": { et: "Vormi-moodul", en: "Forms module" },
  "forms.submissionsPerMonth": { et: "Vormivastuseid/kuus", en: "Form responses/month" },
  "module.chat": { et: "Live-vestlus", en: "Live chat" },
  "module.feedback": { et: "Tagasiside-moodul", en: "Feedback module" },
  "module.portfolio": { et: "Portfoolio-moodul", en: "Portfolio module" },
  "module.artworks": { et: "Kunstiteoste moodul", en: "Artworks module" },
  "module.abtest": { et: "A/B testimise moodul", en: "A/B testing module" },
  "module.appointments": { et: "Ajabroneerimine", en: "Appointment booking" },
  "module.events": { et: "Sündmused", en: "Events" },
  "events.paidTickets": { et: "Tasulised piletid", en: "Paid tickets" },
  "module.recurring": { et: "Püsitellimused", en: "Subscriptions" },
  "module.rentals": { et: "Rendimoodul", en: "Rentals module" },
  "module.payments": { et: "Kaardimaksed", en: "Card payments" },
  "rentals.listings.max": { et: "Rendikuulutusi", en: "Rental listings" },
  "ai.content": { et: "AI sisuloome", en: "AI content" },
  "ai.designAssistant": { et: "Husly AI veebiehitaja", en: "Husly AI Builder" },
  "ai.seo": { et: "AI SEO", en: "AI SEO" },
  "ai.chatbot": { et: "AI vestlusrobot", en: "AI chatbot" },
  "ai.image": { et: "AI pildid", en: "AI images" },
  "ai.translate": { et: "AI tõlge", en: "AI translation" },
  "ai.feedback": { et: "AI tagasiside-abi", en: "AI feedback assistant" },
  "ai.import": { et: "AI-impordi viimistlus", en: "AI import polishing" },
  "ai.credits.monthly": { et: "AI-krediiti/kuus", en: "AI credits/month" },
  "ai.images.monthly": { et: "AI-pilte/kuus", en: "AI images/month" },
  "ai.video": { et: "AI-videod", en: "AI video" },
  "ai.videos.monthly": { et: "AI-videoid/kuus", en: "AI videos/month" },
  "ai.voice": { et: "Artikli ettelugemine", en: "Listen to articles" },
  "ai.voice.charsMonthly": { et: "Stuudiohäält tähemärki/kuus", en: "Studio-voice characters/month" },
  "automation.enabled": { et: "AI-autopiloot", en: "AI autopilot" },
  "automation.recipes.max": { et: "Automaatikaid korraga", en: "Automations at once" },
  "automation.autoApply": { et: "Autopiloot rakendab muudatused ise", en: "Autopilot applies changes itself" },
  "automation.credits.monthly": { et: "Autopiloodi krediiti/kuus", en: "Autopilot credits/month" },
  "automation.custom": { et: "Oma automaatika (kirjelda sõnadega)", en: "Custom automation (describe in words)" },
  "automation.crossSite": { et: "Automaatika mitmel saidil", en: "Automation across sites" },
  "support.priority": { et: "Prioriteetne tugi", en: "Priority support" },
  "sections.accountShare": { et: "Konto-ülesed sektsioonid", en: "Account-wide sections" },
  "integrations.webhooks": { et: "Webhookid (Zapier/Make)", en: "Webhooks (Zapier/Make)" },
  "api.access": { et: "Avalik API + võtmed", en: "Public API + keys" },
  "api.rateLimit": { et: "API päringu-limiit/min", en: "API rate limit/min" },
  "mcp.access": { et: "Claude'i ühendus (MCP)", en: "Claude connection (MCP)" },
  "widget.orbit": { et: "Orbiit-widget", en: "Orbit widget" },
  "widget.scrollGallery": { et: "Kerimisgalerii-widget", en: "Scroll gallery widget" },
  "widget.cardStack": { et: "Virnkaardid-widget", en: "Stack cards widget" },
  "widget.coverflow": { et: "3D-karusselli widget", en: "3D carousel widget" },
  "widget.tiltCards": { et: "3D-särakaartide widget", en: "Spotlight cards widget" },
  "widget.scrollStory": { et: "Kerimisloo widget", en: "Scroll story widget" },
  "widget.kineticEditorial": { et: "Kineetilise ajakirja widget", en: "Kinetic editorial widget" },
  "widget.stardust": { et: "Tähetolmu widget", en: "Stardust widget" },
  "widget.infiniteCanvas": { et: "Lõputu lõuendi widget", en: "Infinite canvas widget" },
  "widget.limelight": { et: "Rambivalguse widget", en: "Limelight widget" },
  "widget.galleryHall": { et: "Galeriisaali widget", en: "Gallery hall widget" },
  "widget.spiralGallery": { et: "Spiraalgalerii widget", en: "Spiral gallery widget" },
  "widget.curator": { et: "Kuraatori widget", en: "Curator widget" },
  "widget.assembly": { et: "Koostu widget", en: "Assembly widget" },
  "widget.cinema": { et: "Filmilindi widget", en: "Cinema reel widget" },
  "widget.blogSphere": { et: "Postituste kera widget", en: "Blog sphere widget" },
  "widget.manifesto": { et: "Manifesti widget", en: "Manifesto widget" },
  "widget.route": { et: "Teekonna widget", en: "Journey widget" },
  "widget.portal": { et: "Portaali widget", en: "Portal widget" },
  "widget.slatReveal": { et: "Ribakardina widget", en: "Slat reveal widget" },
  "widget.deck": { et: "Kaardipaki widget", en: "Card deck widget" },
  "widget.closer": { et: "Lõpuakordi widget", en: "Closer widget" },
  "widget.splitFlap": { et: "Tabloo widget", en: "Split-flap board widget" },
  "widget.configurator": { et: "Hinnakalkulaatori widget", en: "Quote configurator widget" },
  "widget.flowMap": { et: "Vookaardi widget", en: "Flow map widget" },
  // Site monitoring (the Veebihaldus packages; `apps/api/src/lib/monitor-plan.catalog.ts`).
  "monitor.checkMinutes": { et: "Kontrolli sagedus (minutit)", en: "Check interval (minutes)" },
  "monitor.certExpiry": { et: "Sertifikaadi aegumise hoiatus", en: "Certificate expiry warning" },
  "monitor.domainExpiry": { et: "Domeeni aegumise hoiatus", en: "Domain expiry warning" },
  "monitor.speedTest": { et: "Kiiruse mõõtmine (PageSpeed)", en: "Speed test (PageSpeed)" },
  "monitor.wpReporter": { et: "WordPressi raport (plugin)", en: "WordPress report (plugin)" },
  "monitor.securityScan": { et: "Turvaaukude jälgimine", en: "Vulnerability monitoring" },
  "monitor.backupBeforeUpdate": { et: "Varukoopia kontroll enne uuendust", en: "Backup check before updates" },
  "monitor.backupDaily": { et: "Igapäevase varunduse kontroll", en: "Daily backup check" },
  "monitor.dbCleanup": { et: "Andmebaasi puhastuse kontroll", en: "Database clean-up check" },
  "monitor.monthlyReport": { et: "Igakuine seisukorra raport", en: "Monthly status report" },
};

export type T = (key: string) => string;

export function makeT(locale: Locale): T {
  return (key: string) => STRINGS[key]?.[locale] ?? key;
}

export function featureLabel(key: string, locale: Locale): string {
  return FEATURE_LABELS[key]?.[locale] ?? key;
}

/** Short label for a card row (e.g. "Pages" instead of "Pages (max)"); falls back to featureLabel. */
export function featureShortLabel(key: string, locale: Locale): string {
  return FEATURE_SHORT_LABELS[key]?.[locale] ?? featureLabel(key, locale);
}

/** Roll-up line for one exclusive-widget tier, e.g. "Effect widgets (6)". The count is
 *  part of the label on purpose: "Flagship widgets" alone says nothing about how much a
 *  rung adds, and the whole point of collapsing fourteen rows is that the reader still
 *  sees the size of what they get. */
export function widgetTierLabel(tier: 1 | 2 | 3, count: number, locale: Locale): string {
  const name = STRINGS[`compare.widgetTier.${tier}`]?.[locale] ?? `Tier ${tier}`;
  return `${name} (${count})`;
}

/**
 * Human-readable text for a limit/quota value (∞ → "Unlimited", storage → GB/MB, other →
 * localized number). Boolean → "Yes"/"-". Used on the card and in the comparison table.
 */
export function featureValueText(key: string, value: EntitlementValue | undefined, locale: Locale): string {
  if (value === UNLIMITED) return STRINGS["value.unlimited"]?.[locale] ?? "∞";
  if (typeof value === "boolean") return value ? STRINGS["value.yes"]?.[locale] ?? "✓" : "-";
  const n = typeof value === "number" ? value : 0;
  const fmt = locale === "et" ? "et-EE" : "en-US";
  if (key === "storage.mb") {
    return n >= 1000 ? `${(n / 1000).toLocaleString(fmt)} GB` : `${n.toLocaleString(fmt)} MB`;
  }
  return n.toLocaleString(fmt);
}

/** Text from an i18n map in the chosen locale, falling back et → en → first available. */
export function localeText(map: LocaleMap | null | undefined, locale: Locale): string {
  if (!map) return "";
  return map[locale] ?? map.et ?? map.en ?? Object.values(map)[0] ?? "";
}

export function formatMoney(cents: number, currency: string, locale: Locale): string {
  try {
    return new Intl.NumberFormat(locale === "et" ? "et-EE" : "en-US", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function formatDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "-";
  try {
    return new Intl.DateTimeFormat(locale === "et" ? "et-EE" : "en-US", { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Localized label for a plan-change invoice line. Stripe returns the description ONLY in
 * English ("Unused time on X after …"), so we assemble the proration line's label from the
 * structural fields (`kind` + `planName` + `atDate`). For an unknown line (e.g. a regular
 * one, without `kind`) we fall back to Stripe's description.
 */
export function planChangeLineLabel(
  line: { kind?: "unused" | "remaining" | null; planName?: LocaleMap | null; atDate?: string | null; description: string },
  locale: Locale,
): string {
  if (!line.kind || !line.planName) return line.description || "-";
  const t = makeT(locale);
  const time = line.kind === "unused" ? t("confirm.lineUnused") : t("confirm.lineRemaining");
  const head = `${time} ${t("confirm.lineOn")} ${localeText(line.planName, locale)}`;
  return line.atDate ? `${head} ${t("confirm.lineAfter")} ${formatDate(line.atDate, locale)}` : head;
}
